import type {
  AddLinePayload,
  Cart,
  CheckoutCreateResponse,
  CheckoutEmbedErrorCode,
  CheckoutEmbedEvent,
  CheckoutEmbedTerminalPayload,
  CheckoutMountMessage,
  CheckoutPayload,
  CheckoutOptions,
  CheckoutResult,
  CopeOptions,
  CreateCartPayload,
  FieldError,
  MountedCheckout,
  MountCheckoutCallbacks,
  MountCheckoutOptions,
  MountableCheckout,
  OneClickChargeConsentApplicability,
  PostPurchaseChargeStatus,
  PostPurchaseDecisionStatus,
  PostPurchaseOfferStepKind,
  PostPurchaseTerminalStatus,
  PrefilledCheckoutInput,
  PrefilledCheckoutLineInput,
  PrefilledCheckoutResult,
  SdkProduct,
  UpdateBuyerIdentityPayload,
  UpdateLinePayload,
} from './types.js'
import {
  CHECKOUT_EMBED_ERROR_CODES,
  CHECKOUT_EMBED_TERMINAL_STATUSES,
  CopeApiError,
  CopeCartExpiredError,
  CopeError,
  CopeNetworkError,
} from './types.js'
import type { CartState } from './types.js'

const TERMINAL_STATES: ReadonlySet<CartState> = new Set(['ordered', 'expired', 'abandoned', 'merged'])
// The app host: requests go to `${baseUrl}/api/cart/v1/...`, which cope-edge's
// checkout block serves there, and the hosted checkout's origin (derived from
// this URL when checkoutBaseUrl is unset) is the same host.
const DEFAULT_BASE_URL = 'https://app.cope.com'
const STORAGE_KEY = 'cope_sdk_cart'
const REQUEST_TIMEOUT_MS = 8_000
const RETRY_INITIAL_DELAY_MS = 500
const RETRY_MAX_DELAY_MS = 5_000
const CHECKOUT_MESSAGE_SOURCE = 'cope.checkout'
const CHECKOUT_MESSAGE_VERSION = 1
const CHECKOUT_MOUNT_MESSAGE_TYPE = 'copecart.checkout.mount'
const CHECKOUT_MOUNT_MESSAGE_VERSION = 1
const CHECKOUT_MOUNT_RETRY_INTERVAL_MS = 250
const CHECKOUT_MOUNT_MAX_ATTEMPTS = 20
const CHECKOUT_READY_TIMEOUT_MS = 8_000
const SUPPORTED_ONE_CLICK_CHARGE_TERMS_VERSIONS: ReadonlySet<string> = new Set(['oto-v1'])

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'
type MountCleanup = () => void
type RequestOptions = {
  readonly skipAuth?: boolean
  readonly idempotencyKey?: string
}
type PrefilledCheckoutCreateResponse = CheckoutCreateResponse & {
  readonly cart_id: string
  readonly checkout_token?: string
  readonly field_errors?: readonly FieldError[]
}

// One path segment. Ids arrive from callers (and, through cope-sdk/mcp, from AI
// agents); unencoded, `../buyer-identity` or a `?` would aim a request carrying
// the cart secret at another route.
function seg(id: string | number): string {
  return encodeURIComponent(String(id))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

type ApiErrorEntry = { code: string; message: string; field?: string }

// The normalized origin of a URL that is nothing but an origin — no path, query,
// fragment or credentials — or null. `https://App.Cope.com:443/` is accepted and
// becomes `https://app.cope.com`.
function originOnly(value: string): string | null {
  try {
    const url = new URL(value)
    const bare = url.pathname === '/' && !url.search && !url.hash && !url.username && !url.password
    return bare ? url.origin : null
  } catch {
    return null
  }
}

const EDGE_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  payload_too_large: 'Request body is too large',
  rate_limited: 'Too many requests',
  upstream_unavailable: 'The checkout service is unavailable',
  gateway_timeout: 'The checkout service did not answer in time',
  not_found: 'Not found',
}

// Two envelopes reach the SDK: cope-core's `{errors: [{code, message}]}`, and
// the `{error: "<code>", request_id}` cope-edge answers itself on the checkout
// block — rate limit, body cap, unreachable upstream, deadline
// (svcs/cope-edge/docs/errors.md). Plan 30 W2-47.
function parseErrorBody(body: unknown): ApiErrorEntry[] | null {
  if (!isRecord(body)) return null
  if (Array.isArray(body.errors) && body.errors.length > 0 && body.errors.every(isApiErrorEntry)) return body.errors
  if (typeof body.error === 'string' && body.error.length > 0) {
    // Edge's `error` is a snake_case code; anything else is prose some other
    // upstream put there, which must not masquerade as a code callers switch on.
    return /^[a-z][a-z0-9_]*$/.test(body.error)
      ? [{ code: body.error, message: EDGE_ERROR_MESSAGES[body.error] ?? body.error }]
      : [{ code: 'unknown', message: body.error }]
  }
  return null
}

function isApiErrorEntry(value: unknown): value is ApiErrorEntry {
  return isRecord(value) && typeof value.code === 'string' && typeof value.message === 'string'
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// asctime, the one HTTP-date form that names no zone: `Sun Nov  6 08:49:37 1994`.
const ASCTIME = /^[A-Z][a-z]{2} ([A-Z][a-z]{2}) ([ \d]\d) (\d{2}):(\d{2}):(\d{2}) (\d{4})$/

// NaN for a date that does not exist: Date.UTC rolls an unknown month (-1), day 39
// or hour 99 over into a real instant, so the fields must survive a round trip.
function asctimeToUtc([, monthName, day, hh, mm, ss, year]: RegExpExecArray): number {
  const fields = [Number(year), MONTHS.indexOf(monthName ?? ''), Number((day ?? '').trim()), Number(hh), Number(mm), Number(ss)]
  const at = Date.UTC(fields[0], fields[1], fields[2], fields[3], fields[4], fields[5])
  const back = new Date(at)
  const roundTrip = [back.getUTCFullYear(), back.getUTCMonth(), back.getUTCDate(), back.getUTCHours(),
    back.getUTCMinutes(), back.getUTCSeconds()]
  return roundTrip.every((value, i) => value === fields[i]) ? at : Number.NaN
}

// `Retry-After` is either delta-seconds or an HTTP-date (RFC 9110 §10.2.3), and
// every HTTP-date is GMT. `Date.parse` reads the asctime form in the HOST's zone,
// so that form is read by hand; the other two say `GMT`, which Date.parse honours.
// Anything else is ignored rather than guessed at.
function parseRetryAfter(value: string | null): number | null {
  if (value === null) return null
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed)
  const asctime = ASCTIME.exec(trimmed)
  const at = asctime ? asctimeToUtc(asctime) : / GMT$/.test(trimmed) ? Date.parse(trimmed) : Number.NaN
  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000))
}

function bodyRequestId(body: unknown): string | null {
  return isRecord(body) && typeof body.request_id === 'string' ? body.request_id : null
}

function normalizeOneClickChargeConsent(value: unknown): OneClickChargeConsentApplicability {
  if (!isRecord(value)) {
    return { applicable: false, terms_version: null }
  }

  const termsVersion = value.terms_version
  if (
    value.applicable !== true
    || typeof termsVersion !== 'string'
    || !SUPPORTED_ONE_CLICK_CHARGE_TERMS_VERSIONS.has(termsVersion)
  ) {
    return { applicable: false, terms_version: null }
  }

  return { applicable: true, terms_version: termsVersion }
}

function normalizeCart(cart: Cart): Cart {
  return {
    ...cart,
    one_click_charge_consent: normalizeOneClickChargeConsent(cart.one_click_charge_consent),
  }
}

// Both guards read the exported protocol lists rather than repeating the
// union: a second hand-maintained copy is exactly how `already_completed`
// came to be emitted by the bridge and dropped here.
const CHECKOUT_EMBED_ERROR_CODE_SET: ReadonlySet<string> = new Set(CHECKOUT_EMBED_ERROR_CODES)
const CHECKOUT_EMBED_TERMINAL_STATUS_SET: ReadonlySet<string> = new Set(CHECKOUT_EMBED_TERMINAL_STATUSES)

function isCheckoutEmbedErrorCode(value: string): value is CheckoutEmbedErrorCode {
  return CHECKOUT_EMBED_ERROR_CODE_SET.has(value)
}

function isCheckoutEmbedTerminalStatus(value: string): value is CheckoutEmbedTerminalPayload['status'] {
  return CHECKOUT_EMBED_TERMINAL_STATUS_SET.has(value)
}

function isPostPurchaseOfferStepKind(value: string): value is PostPurchaseOfferStepKind {
  return value === 'upsell' || value === 'downsell'
}

function isPostPurchaseDecisionStatus(value: string): value is PostPurchaseDecisionStatus {
  return (
    value === 'accepted'
    || value === 'declined'
    || value === 'failed_not_charged'
    || value === 'requires_action'
  )
}

function isPostPurchaseChargeStatus(value: string): value is PostPurchaseChargeStatus {
  return (
    value === 'not_charged'
    || value === 'charged'
    || value === 'requires_action'
    || value === 'failed'
  )
}

function isPostPurchaseTerminalStatus(value: string): value is PostPurchaseTerminalStatus {
  return (
    value === 'completed'
    || value === 'declined'
    || value === 'expired'
    || value === 'unavailable'
  )
}

function currentWindowOrigin(): string {
  const location = window.location
  if (location.origin) return location.origin

  const host = location.host || [location.hostname, location.port].filter(Boolean).join(':')
  return new URL(`${location.protocol}//${host}`).origin
}

function normalizeCheckoutUrls(absoluteUrl: string): { checkoutUrl: string; embedCheckoutUrl: string } {
  const checkoutUrl = new URL(absoluteUrl)
  const embedCheckoutUrl = new URL(absoluteUrl)
  const embedMatch = checkoutUrl.pathname.match(/^\/checkout\/embed\/([^/]+)$/)
  const checkoutMatch = checkoutUrl.pathname.match(/^\/checkout\/([^/]+)$/)

  if (embedMatch) {
    checkoutUrl.pathname = `/checkout/${embedMatch[1]}`
    embedCheckoutUrl.pathname = `/checkout/embed/${embedMatch[1]}`
    return {
      checkoutUrl: checkoutUrl.toString(),
      embedCheckoutUrl: embedCheckoutUrl.toString(),
    }
  }

  if (checkoutMatch) {
    checkoutUrl.pathname = `/checkout/${checkoutMatch[1]}`
    embedCheckoutUrl.pathname = `/checkout/embed/${checkoutMatch[1]}`
    return {
      checkoutUrl: checkoutUrl.toString(),
      embedCheckoutUrl: embedCheckoutUrl.toString(),
    }
  }

  throw new CopeError('Checkout URL path cannot be mapped to COPE checkout routes')
}

function checkoutEmbedToken(embedCheckoutUrl: string): string {
  const url = new URL(embedCheckoutUrl)
  const match = url.pathname.match(/^\/checkout\/embed\/([^/]+)$/)
  if (!match) {
    throw new CopeError('Checkout embed URL path cannot be mapped to COPE checkout embed route')
  }

  return decodeURIComponent(match[1])
}

function checkoutEmbedOrigin(raw: CheckoutCreateResponse): string | undefined {
  const embed = raw.embed
  if (!isRecord(embed)) return undefined
  if (embed.allowed !== true) return undefined
  if (typeof embed.parent_origin !== 'string') return undefined

  try {
    const origin = new URL(embed.parent_origin).origin
    return origin === embed.parent_origin ? embed.parent_origin : undefined
  } catch {
    return undefined
  }
}

function parseCheckoutEmbedEvent(data: unknown): CheckoutEmbedEvent | null {
  if (!isRecord(data)) return null
  if (data.source !== CHECKOUT_MESSAGE_SOURCE) return null
  if (data.version !== CHECKOUT_MESSAGE_VERSION) return null

  const metadata = {
    ...(typeof data.messageId === 'string' ? { messageId: data.messageId } : {}),
    ...(typeof data.timestamp === 'string' ? { timestamp: data.timestamp } : {}),
  }

  if (data.type === 'ready') {
    return { type: 'ready', ...metadata }
  }

  if (data.type === 'resize') {
    if (!isRecord(data.payload)) return null
    const { height } = data.payload
    if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) return null
    return {
      type: 'resize',
      payload: { height: Math.ceil(height) },
      ...metadata,
    }
  }

  if (data.type === 'error') {
    if (!isRecord(data.payload)) return null
    const { code, retryable } = data.payload
    if (typeof code !== 'string' || !isCheckoutEmbedErrorCode(code)) return null
    if (typeof retryable !== 'boolean') return null
    return {
      type: 'error',
      payload: { code, retryable },
      ...metadata,
    }
  }

  if (data.type === 'terminal') {
    if (!isRecord(data.payload)) return null
    const { status } = data.payload
    if (typeof status !== 'string' || !isCheckoutEmbedTerminalStatus(status)) return null
    return {
      type: 'terminal',
      payload: { status },
      ...metadata,
    }
  }

  // Only recognized fields are ever read off `data.payload` below — this is what
  // keeps the sanitization guarantee: any extra keys the iframe message might
  // carry (token, cart_secret, client_secret, email, ...) are never copied into
  // the object handed to callbacks.
  if (data.type === 'post_purchase_offer') {
    if (!isRecord(data.payload)) return null
    const { offerRef, stepKind, title, priceGrossMinorUnits, currency } = data.payload
    if (typeof offerRef !== 'string' || !offerRef) return null
    if (typeof stepKind !== 'string' || !isPostPurchaseOfferStepKind(stepKind)) return null
    if (typeof title !== 'string' || !title) return null
    if (typeof priceGrossMinorUnits !== 'number' || !Number.isFinite(priceGrossMinorUnits) || priceGrossMinorUnits < 0) return null
    if (typeof currency !== 'string' || !currency) return null
    return {
      type: 'post_purchase_offer',
      payload: { offerRef, stepKind, title, priceGrossMinorUnits, currency },
      ...metadata,
    }
  }

  if (data.type === 'post_purchase_decision') {
    if (!isRecord(data.payload)) return null
    const { offerRef, status, chargeStatus } = data.payload
    if (typeof offerRef !== 'string' || !offerRef) return null
    if (typeof status !== 'string' || !isPostPurchaseDecisionStatus(status)) return null
    if (typeof chargeStatus !== 'string' || !isPostPurchaseChargeStatus(chargeStatus)) return null
    return {
      type: 'post_purchase_decision',
      payload: { offerRef, status, chargeStatus },
      ...metadata,
    }
  }

  if (data.type === 'post_purchase_terminal') {
    if (!isRecord(data.payload)) return null
    const { status } = data.payload
    if (typeof status !== 'string' || !isPostPurchaseTerminalStatus(status)) return null
    return {
      type: 'post_purchase_terminal',
      payload: { status },
      ...metadata,
    }
  }

  return null
}

/**
 * COPE SDK — browser client for the checkout API cope-edge serves under `/api/cart/v1`.
 *
 * Usage:
 * ```ts
 * const cope = new Cope({ publishableKey: 'cope_pk_...' })
 * const cart = await cope.createCart({ currency: 'EUR' })
 * await cope.addLine(cart.id, { product_id: 'uuid-123' })
 * await cope.reprice(cart.id)
 * const checkout = await cope.checkout(cart.id, { success_url: '...', cancel_url: '...', consents: [{ type: 'buyer_tos' }] })
 * cope.redirectToCheckout(checkout)
 * ```
 */
export class Cope {
  private readonly publishableKey: string
  private readonly baseUrl: string
  private readonly checkoutBaseUrl: string
  private readonly persistence: 'localStorage' | 'none'
  private readonly abortController: AbortController
  private readonly mountCleanups = new Set<MountCleanup>()
  private cachedSecret: string | null = null
  private cachedCartId: string | null = null

  /** The active cart ID from localStorage, or null if no cart exists. */
  get currentCartId(): string | null {
    return this.cachedCartId
  }

  constructor(options: CopeOptions) {
    if (!options.publishableKey?.startsWith('cope_pk_')) {
      throw new CopeError('Invalid publishableKey. Must start with "cope_pk_".')
    }

    if (typeof window !== 'undefined' && window.location?.protocol === 'http:' && window.location.hostname !== 'localhost') {
      throw new CopeError('COPE SDK requires HTTPS. Refusing to operate on an insecure page.')
    }

    this.publishableKey = options.publishableKey
    // An origin only. @copecart/sdk's docs told integrators to pass
    // `.../gateway/cart_api`; carried over, it would 404 on every call rather
    // than fail here where the cause is visible.
    const baseOrigin = originOnly(options.baseUrl ?? DEFAULT_BASE_URL)
    if (baseOrigin === null) {
      throw new CopeError('Invalid baseUrl. Pass an origin such as "https://app.cope.com"; the SDK adds /api/cart/v1 itself.')
    }
    this.baseUrl = baseOrigin
    this.checkoutBaseUrl = options.checkoutBaseUrl
      ? options.checkoutBaseUrl.replace(/\/+$/, '')
      : new URL(this.baseUrl).origin
    this.abortController = new AbortController()
    // A client handed a cart acts on that cart only: it must not read, overwrite or
    // clear the page's stored cart, which belongs to whichever client created it.
    this.persistence = options.cart ? 'none' : (options.persistence ?? 'localStorage')

    if (options.cart) {
      this.cachedCartId = options.cart.id
      this.cachedSecret = options.cart.secret
    } else {
      this.hydrateFromStorage()
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  async createCart(payload: CreateCartPayload): Promise<Cart> {
    return this.createCartRequest(payload, { skipAuth: true })
  }

  async getProduct(productUuid: string): Promise<SdkProduct> {
    return this.get<SdkProduct>(`/products/${seg(productUuid)}`, { skipAuth: true })
  }

  async getCart(cartId: string): Promise<Cart> {
    const cart = normalizeCart(await this.get<Cart>(`/carts/${seg(cartId)}`))
    if (TERMINAL_STATES.has(cart.state)) {
      this.clearStorage()
      throw new CopeCartExpiredError(cart.state)
    }
    return cart
  }

  async addLine(cartId: string, payload: AddLinePayload): Promise<Cart> {
    return normalizeCart(await this.post<Cart>(`/carts/${seg(cartId)}/lines`, payload))
  }

  /** `lineId` is the line's `id`, an integer; a string of its digits is accepted too. */
  async updateLine(cartId: string, lineId: number | string, payload: UpdateLinePayload): Promise<Cart> {
    return normalizeCart(await this.patch<Cart>(`/carts/${seg(cartId)}/lines/${seg(lineId)}`, payload))
  }

  /** `lineId` is the line's `id`, an integer; a string of its digits is accepted too. */
  async removeLine(cartId: string, lineId: number | string): Promise<Cart> {
    return normalizeCart(await this.del<Cart>(`/carts/${seg(cartId)}/lines/${seg(lineId)}`))
  }

  async setBuyerIdentity(cartId: string, payload: UpdateBuyerIdentityPayload): Promise<Cart> {
    return normalizeCart(await this.patch<Cart>(`/carts/${seg(cartId)}/buyer-identity`, payload))
  }

  async reprice(cartId: string): Promise<Cart> {
    return normalizeCart(await this.post<Cart>(`/carts/${seg(cartId)}/reprice`, {}))
  }

  async applyPromoCode(cartId: string, code: string): Promise<Cart> {
    return normalizeCart(await this.post<Cart>(`/carts/${seg(cartId)}/promo_code`, { code }))
  }

  async removePromoCode(cartId: string): Promise<Cart> {
    return normalizeCart(await this.del<Cart>(`/carts/${seg(cartId)}/promo_code`))
  }

  async checkout(cartId: string, payload: CheckoutPayload, options: CheckoutOptions = {}): Promise<CheckoutResult> {
    return this.createCheckoutResult(cartId, payload,
      options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : undefined)
  }

  async createPrefilledCheckout(input: PrefilledCheckoutInput): Promise<PrefilledCheckoutResult> {
    const lines = this.normalizePrefilledLines(input)
    const raw = await this.post<PrefilledCheckoutCreateResponse>('/prefilled-checkouts', {
      external_reference: input.externalReference,
      currency: input.currency,
      ...(input.locale != null ? { locale: input.locale } : {}),
      ...(input.attribution ? { attribution: input.attribution } : {}),
      ...(input.intendedPaymentMethod != null ? { intended_payment_method: input.intendedPaymentMethod } : {}),
      ...(input.metadata ? { metadata: input.metadata } : {}),
      ...(input.collectBuyerIdentityAtCheckout != null
        ? { collect_buyer_identity_at_checkout: input.collectBuyerIdentityAtCheckout }
        : {}),
      lines: lines.map((line) => ({
        product_id: line.productId,
        ...(line.planId !== undefined ? { plan_id: line.planId } : {}),
        ...(line.quantity !== undefined ? { quantity: line.quantity } : {}),
      })),
      buyer_identity: input.buyerIdentity,
      checkout: input.checkout,
    }, {
      skipAuth: true,
      idempotencyKey: await this.prefilledIdempotencyKey(input.externalReference),
    })
    const checkout = this.normalizeCheckoutResult(raw)

    return {
      cartId: raw.cart_id,
      cartVersion: raw.cart_version,
      checkoutId: checkout.checkoutId,
      checkoutToken: raw.checkout_token ?? checkoutEmbedToken(checkout.embedCheckoutUrl),
      checkoutUrl: checkout.checkoutUrl,
      embedCheckoutUrl: checkout.embedCheckoutUrl,
      ...(checkout.embedOrigin ? { embedOrigin: checkout.embedOrigin } : {}),
      status: checkout.status,
      expiresAt: checkout.expiresAt,
      totals: checkout.totals,
      ...(input.intendedPaymentMethod != null ? { intendedPaymentMethod: input.intendedPaymentMethod } : {}),
      ...(raw.field_errors?.length ? { fieldErrors: raw.field_errors } : {}),
    }
  }

  async cancelCheckout(checkoutId: string): Promise<void> {
    await this.requestVoid('POST', `/checkouts/${seg(checkoutId)}/cancel`)
  }

  redirectToCheckout(checkout: CheckoutResult): void {
    if (this.abortController.signal.aborted) return
    if (typeof window !== 'undefined') {
      window.location.href = checkout.checkoutUrl
    }
  }

  mountCheckout(checkout: CheckoutResult | MountableCheckout, options: MountCheckoutOptions): MountedCheckout
  mountCheckout(target: HTMLElement | string, checkout: CheckoutResult | MountableCheckout, options?: MountCheckoutCallbacks): MountedCheckout
  mountCheckout(
    checkoutOrTarget: CheckoutResult | MountableCheckout | HTMLElement | string,
    optionsOrCheckout: MountCheckoutOptions | CheckoutResult | MountableCheckout,
    maybeOptions: MountCheckoutCallbacks = {},
  ): MountedCheckout {
    if (this.abortController.signal.aborted) {
      throw new CopeError('SDK destroyed')
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new CopeError('mountCheckout requires a browser environment')
    }

    const { checkout, options } = this.normalizeMountCheckoutArgs(
      checkoutOrTarget,
      optionsOrCheckout,
      maybeOptions,
    )
    // Validation first: every check below reads caller-supplied input, and until
    // this method accepted a bare embed URL that input was origin-proven by
    // construction — it could only have come from normalizeCheckoutResult, which
    // refuses a foreign origin. Accepting `embed_url` straight out of an API
    // response removes that proof, so the two URLs are re-checked here before any
    // DOM is touched or any token is parsed.
    const allowedOrigin = this.checkoutOrigin()
    this.assertCopeOrigin(checkout.embedCheckoutUrl, allowedOrigin, 'Checkout embed URL')

    if (!checkout.embedOrigin) {
      throw new CopeError(
        'mountCheckout requires checkout.embedOrigin — send embed_origin on checkout() or on '
        + 'create-offer, and register the origin in dashboard Settings',
      )
    }

    // The `checkoutUrl` sink is strictly worse than the frame's: `fallback:
    // 'redirect'` navigates the buyer's TOP window, so an unvalidated value would
    // hand the whole page to another origin mid-checkout.
    //
    // It is also deliberately NOT derived from the embed URL. For an offer the
    // recovery page is `public_url` (/offers/:uuid), which renders the offer's
    // state; the hosted checkout route would refuse in exactly the cases the
    // fallback exists for, and it carries the payment-capable access token into the
    // address bar and browser history. Refuse at mount rather than at the buyer's
    // eight-second timeout, so this is an integration error, never a runtime one.
    let fallbackUrl: string | undefined
    if (options.fallback === 'redirect') {
      if (!checkout.checkoutUrl) {
        throw new CopeError(
          "mountCheckout with fallback: 'redirect' requires checkout.checkoutUrl — for an offer, "
          + 'pass its public_url',
        )
      }
      this.assertCopeOrigin(checkout.checkoutUrl, allowedOrigin, 'Checkout URL')
      fallbackUrl = checkout.checkoutUrl
    }

    const target = this.resolveMountTarget(options.target)
    const iframe = document.createElement('iframe')
    const checkoutOrigin = new URL(checkout.embedCheckoutUrl).origin
    const mountMessage: CheckoutMountMessage = {
      type: CHECKOUT_MOUNT_MESSAGE_TYPE,
      version: CHECKOUT_MOUNT_MESSAGE_VERSION,
      token: checkoutEmbedToken(checkout.embedCheckoutUrl),
    }
    const parentOrigin = currentWindowOrigin()
    let destroyed = false
    let ready = false
    let mountAttempts = 0
    // The DOM timer id. Not ReturnType<typeof window.setInterval>: that picks
    // the last overload, which is Node's Timeout once @types/node is loaded.
    let mountRetry: number | undefined
    let readyTimeout: number | undefined

    if (checkout.embedOrigin !== parentOrigin) {
      throw new CopeError(`Checkout embed origin ${checkout.embedOrigin} does not match current origin ${parentOrigin}`)
    }

    iframe.src = checkout.embedCheckoutUrl
    iframe.title = options.title ?? 'COPE checkout'
    iframe.setAttribute('allow', 'payment *')
    iframe.referrerPolicy = 'no-referrer'
    iframe.style.border = '0'
    iframe.style.width = '100%'
    iframe.style.minHeight = '720px'
    if (options.className) {
      iframe.className = options.className
    }

    const clearMountRetry = () => {
      if (mountRetry !== undefined) {
        window.clearInterval(mountRetry)
        mountRetry = undefined
      }
    }

    const clearReadyTimeout = () => {
      if (readyTimeout !== undefined) {
        window.clearTimeout(readyTimeout)
        readyTimeout = undefined
      }
    }

    const cleanup = () => {
      if (destroyed) return
      destroyed = true
      clearMountRetry()
      clearReadyTimeout()
      window.removeEventListener('message', handleMessage)
      iframe.removeEventListener('load', handleIframeLoad)
      iframe.remove()
      this.mountCleanups.delete(cleanup)
    }

    const postMountMessage = () => {
      if (destroyed || ready) return
      if (mountAttempts >= CHECKOUT_MOUNT_MAX_ATTEMPTS) {
        clearMountRetry()
        return
      }

      mountAttempts += 1
      iframe.contentWindow?.postMessage(mountMessage, checkoutOrigin)
    }

    const startMountHandshake = () => {
      if (destroyed) return
      mountAttempts = 0
      clearMountRetry()
      postMountMessage()
      mountRetry = window.setInterval(postMountMessage, CHECKOUT_MOUNT_RETRY_INTERVAL_MS)
    }

    /**
     * Re-arm the handshake on every completed navigation inside the frame.
     *
     * A redirect payment method (Klarna / iDEAL / other BNPL) navigates the
     * iframe away to the provider and back to `/checkout/embed/<token>`. The
     * returning document is a brand-new bridge sitting in `awaiting-mount`, and
     * it will not proceed until the parent proves it knows the token again.
     * Without this the embed hangs forever after any redirect payment.
     *
     * `load` fires on the parent for cross-origin navigations too, so this also
     * runs while the frame is on the provider's page. That is safe: the mount
     * message is posted with `targetOrigin = checkoutOrigin`, so the browser
     * drops it rather than leaking the checkout token to the provider.
     *
     * The ready timeout is deliberately NOT re-armed. It is a one-shot
     * "the embed never loaded" guard for the initial mount. Re-arming it would
     * fire `onError({ code: 'load_failed' })` — or, under
     * `fallback: 'redirect'`, navigate the vendor's top window away — eight
     * seconds after the buyer lands on the provider's page, mid-payment. The
     * parent cannot tell those two loads apart: a cross-origin frame exposes
     * neither its location nor whether a `postMessage` was delivered.
     *
     * So the returning document owns reporting its own failure instead: the
     * bridge posts `error` even when it blocks before it has a validated
     * policy, which is why the parent can drop the timer here without going
     * silent after a redirect payment. The one case neither side covers is a
     * return the browser refuses to render at all (the embed origin was
     * disabled while the buyer was at the provider, so the response carries
     * `frame-ancestors 'none'`): no document runs, and nothing can report.
     */
    const handleIframeLoad = () => {
      if (destroyed) return
      ready = false
      startMountHandshake()
    }

    const handleMessage = (event: MessageEvent<unknown>) => {
      if (destroyed) return
      if (event.origin !== checkoutOrigin) return
      if (event.source !== iframe.contentWindow) return

      const message = parseCheckoutEmbedEvent(event.data)
      if (!message) return

      options.onMessage?.(message)
      if (message.type === 'ready') {
        ready = true
        clearMountRetry()
        clearReadyTimeout()
        options.onReady?.()
      } else if (message.type === 'resize') {
        iframe.style.height = `${message.payload.height}px`
        options.onResize?.(message.payload)
      } else if (message.type === 'error') {
        // A bridge that reports an error has already accepted the handshake, so
        // there is nothing left to retry and no missing load to time out. Both
        // timers must stop here or the ready timeout still fires eight seconds
        // later with a bogus `load_failed` (or a top-window fallback redirect).
        clearMountRetry()
        clearReadyTimeout()
        options.onError?.(message.payload)
      } else if (message.type === 'terminal') {
        clearMountRetry()
        clearReadyTimeout()
        options.onTerminal?.(message.payload)
        // Only a live `completed` is a success. `already_completed` means the
        // buyer paid before this mount, so re-running vendor fulfilment would
        // double-grant; `processing` is an asynchronous rail that has not
        // settled yet.
        if (message.payload.status === 'completed') {
          options.onSuccess?.()
        } else if (message.payload.status === 'cancelled') {
          options.onCancel?.()
        }
      } else if (message.type === 'post_purchase_offer') {
        options.onPostPurchaseOffer?.(message.payload)
      } else if (message.type === 'post_purchase_decision') {
        options.onPostPurchaseDecision?.(message.payload)
      } else if (message.type === 'post_purchase_terminal') {
        options.onPostPurchaseTerminal?.(message.payload)
      }
    }

    window.addEventListener('message', handleMessage)
    iframe.addEventListener('load', handleIframeLoad)
    this.mountCleanups.add(cleanup)

    try {
      target.appendChild(iframe)
      startMountHandshake()
      readyTimeout = window.setTimeout(() => {
        if (destroyed || ready) return
        if (options.fallback === 'redirect') {
          options.onFallbackRedirect?.()
          window.location.href = fallbackUrl as string
          return
        }

        options.onError?.({ code: 'load_failed', retryable: true })
      }, options.readyTimeoutMs ?? CHECKOUT_READY_TIMEOUT_MS)
    } catch (error) {
      cleanup()
      throw error
    }

    return { iframe, destroy: cleanup }
  }

  destroy(): void {
    for (const cleanup of Array.from(this.mountCleanups)) {
      cleanup()
    }
    this.abortController.abort()
    this.clearStorage()
  }

  // ---------------------------------------------------------------------------
  // HTTP methods
  // ---------------------------------------------------------------------------

  private async createCartRequest(payload: CreateCartPayload, opts?: RequestOptions): Promise<Cart> {
    // POST /carts renders the cart without `lines` or `buyer_identity`
    // (apps/cope-core carts_controller.rb `create`: CartBlueprint's default view,
    // unmasked). A new cart has neither, and the payload cannot supply them.
    const created = await this.post<Cart>('/carts', payload, opts)
    const cart = normalizeCart({ ...created, lines: created.lines ?? [], buyer_identity: created.buyer_identity ?? null })
    if (cart.cart_secret) {
      this.cachedCartId = cart.id
      this.cachedSecret = cart.cart_secret
      this.persistToStorage()
    }
    return cart
  }

  private async createCheckoutResult(cartId: string, payload: CheckoutPayload, opts?: RequestOptions): Promise<CheckoutResult> {
    const raw = await this.post<CheckoutCreateResponse>(`/carts/${seg(cartId)}/checkout`, payload, opts)
    return this.normalizeCheckoutResult(raw)
  }

  private normalizeCheckoutResult(raw: CheckoutCreateResponse): CheckoutResult {
    const absoluteUrl = raw.checkout_url.startsWith('http')
      ? raw.checkout_url
      : `${this.checkoutBaseUrl}${raw.checkout_url}`

    // Validate redirect URL belongs to expected origin
    this.assertCopeOrigin(absoluteUrl, this.checkoutOrigin(), 'Checkout URL')

    const urls = normalizeCheckoutUrls(absoluteUrl)

    return {
      checkoutId: raw.checkout_id,
      checkoutUrl: urls.checkoutUrl,
      embedCheckoutUrl: urls.embedCheckoutUrl,
      embedOrigin: checkoutEmbedOrigin(raw),
      status: raw.status,
      expiresAt: raw.expires_at,
      totals: raw.totals,
    }
  }

  private async requestVoid(method: HttpMethod, path: string, body?: unknown): Promise<void> {
    await this.request<unknown>(method, path, body)
  }

  private async get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, opts)
  }

  private async post<T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, opts)
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body)
  }

  private async del<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path)
  }

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<T> {
    if (this.abortController.signal.aborted) {
      throw new CopeError('SDK destroyed')
    }

    const url = `${this.baseUrl}/api/cart/v1${path}`
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Cope-Key': this.publishableKey,
    }

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    if (!opts?.skipAuth && this.cachedSecret) {
      headers['Authorization'] = `CartSecret ${this.cachedSecret}`
    }

    // Idempotency key for POST — same key across retries
    const idempotencyKey = method === 'POST' ? (opts?.idempotencyKey ?? crypto.randomUUID()) : undefined
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey
    }

    const maxAttempts = (method === 'GET' || (method === 'POST' && this.isRetryablePath(path))) ? 2 : 1
    // A retry after an ambiguous failure (5xx, timeout, network) may re-run a write the
    // server already committed; only a GET, or a POST cope-core deduplicates by
    // Idempotency-Key, survives that. A 429 is refused before processing, so it is safe.
    const replaySafe = this.isReplaySafe(method, path)
    let lastError: unknown

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        const delay = this.calculateRetryDelay(attempt - 1)
        await this.sleep(delay)
      }

      const timeoutController = new AbortController()
      const timeout = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS)
      const signal = AbortSignal.any([this.abortController.signal, timeoutController.signal])

      try {
        const response = await fetch(url, {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          signal,
        })

        if (response.ok) {
          if (response.status === 204) return undefined as T
          return (await response.json()) as T
        }

        // Parse error body
        const errorBody: unknown = await response.json().catch(() => null)
        const errors = parseErrorBody(errorBody) ?? [{ code: 'unknown', message: response.statusText }]
        const requestId = response.headers.get('X-Request-Id') ?? bodyRequestId(errorBody)
        const retryAfter = parseRetryAfter(response.headers.get('Retry-After'))

        // Retry on 5xx / 429
        if (this.shouldRetry(response.status) && attempt < maxAttempts - 1 && (replaySafe || response.status === 429)) {
          lastError = new CopeApiError(response.status, errors, requestId, retryAfter)
          continue
        }

        throw new CopeApiError(response.status, errors, requestId, retryAfter)
      } catch (error) {
        if (error instanceof CopeApiError) throw error
        if (error instanceof CopeCartExpiredError) throw error

        // AbortError from SDK destroy
        if (this.abortController.signal.aborted) {
          throw new CopeError('SDK destroyed')
        }

        // Network or timeout error — retry only where a replay cannot duplicate a write
        if (attempt < maxAttempts - 1 && replaySafe) {
          lastError = error
          continue
        }

        const msg = error instanceof Error ? error.message : 'Network error'
        throw new CopeNetworkError(msg)
      } finally {
        clearTimeout(timeout)
      }
    }

    // Should not reach here, but just in case
    throw lastError instanceof Error ? lastError : new CopeNetworkError('Request failed')
  }

  private shouldRetry(status: number): boolean {
    return status === 429 || status >= 500
  }

  private isRetryablePath(path: string): boolean {
    return !path.includes('/checkout')
  }

  // The SDK POSTs cope-core's IdempotencyMiddleware deduplicates
  // (CART_POST_ROUTES, apps/cope-core/app/middleware/idempotency_middleware.rb).
  // Checkout creation is deduplicated too, but isRetryablePath keeps it single-shot.
  private isReplaySafe(method: string, path: string): boolean {
    return method === 'GET' || (method === 'POST' && path === '/prefilled-checkouts')
  }

  private calculateRetryDelay(attempt: number): number {
    const exponential = RETRY_INITIAL_DELAY_MS * Math.pow(2, attempt)
    const capped = Math.min(exponential, RETRY_MAX_DELAY_MS)
    return capped * (0.5 + Math.random() * 0.5)
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private normalizePrefilledLines(input: PrefilledCheckoutInput): readonly PrefilledCheckoutLineInput[] {
    if (!isRecord(input)) {
      throw new CopeError('createPrefilledCheckout requires an input object')
    }

    if (!input.externalReference) {
      throw new CopeError('createPrefilledCheckout requires externalReference for backend idempotency')
    }

    if (input.line != null && input.lines != null) {
      throw new CopeError('Use either line or lines for createPrefilledCheckout, not both')
    }

    if (input.line != null) {
      return [input.line]
    }

    if (input.lines == null || input.lines.length === 0) {
      throw new CopeError('createPrefilledCheckout requires at least one line')
    }

    return input.lines
  }

  // Injective in the reference: two different references never share a key.
  // A reference that is already header-safe and short keeps the readable
  // `prefilled:<reference>:cart`; any other is SHA-256'd whole, under a `#`
  // prefix no readable key can contain, instead of being rewritten or cut —
  // `INV/2026/1` and `INV-2026-1` used to share a key, and the second checkout
  // got 422 idempotency_body_mismatch for the replay TTL.
  private async prefilledIdempotencyKey(externalReference: string): Promise<string> {
    if (/^[A-Za-z0-9._:-]{1,120}$/.test(externalReference)) return `prefilled:${externalReference}:cart`
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(externalReference))
    const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
    return `prefilled#${hex}:cart`
  }

  // One definition of "an origin this SDK is configured to talk to", used by the
  // mount guards and by normalizeCheckoutResult, so the two cannot drift.
  private checkoutOrigin(): string {
    return new URL(this.checkoutBaseUrl).origin
  }

  private assertCopeOrigin(url: string, allowedOrigin: string, label: string): void {
    const origin = new URL(url).origin
    if (origin !== allowedOrigin) {
      throw new CopeError(`${label} origin ${origin} does not match expected ${allowedOrigin}`)
    }
  }

  private resolveMountTarget(target: HTMLElement | string): HTMLElement {
    // The IIFE bundle ships to untyped JS, where the checkout-first form can be
    // called with the callbacks object alone. Without this the mount got as far as
    // `undefined.appendChild` and surfaced a raw DOM TypeError instead of saying
    // what was missing.
    if (target == null) {
      throw new CopeError('mountCheckout requires options.target')
    }
    if (typeof target !== 'string') return target

    const element = document.querySelector(target)
    if (element instanceof HTMLElement) return element

    throw new CopeError(`mountCheckout target "${target}" was not found`)
  }

  private normalizeMountCheckoutArgs(
    checkoutOrTarget: CheckoutResult | MountableCheckout | HTMLElement | string,
    optionsOrCheckout: MountCheckoutOptions | CheckoutResult | MountableCheckout,
    maybeOptions: MountCheckoutCallbacks,
  ): { checkout: CheckoutResult | MountableCheckout; options: MountCheckoutOptions } {
    if (this.isMountableCheckout(checkoutOrTarget)) {
      return {
        checkout: checkoutOrTarget,
        options: optionsOrCheckout as MountCheckoutOptions,
      }
    }

    if (!this.isMountableCheckout(optionsOrCheckout)) {
      throw new CopeError('mountCheckout requires a checkout with embedCheckoutUrl')
    }

    return {
      checkout: optionsOrCheckout,
      options: { ...maybeOptions, target: checkoutOrTarget },
    }
  }

  // Also the overload discriminator: the target-first form passes an element or
  // a selector string, neither of which carries an embedCheckoutUrl.
  private isMountableCheckout(value: unknown): value is CheckoutResult | MountableCheckout {
    if (!isRecord(value)) return false
    return typeof value.embedCheckoutUrl === 'string'
  }

  // ---------------------------------------------------------------------------
  // localStorage persistence
  // ---------------------------------------------------------------------------

  private get storageEnabled(): boolean {
    return this.persistence === 'localStorage' && typeof localStorage !== 'undefined'
  }

  private hydrateFromStorage(): void {
    if (!this.storageEnabled) return
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as { cartId?: string; secret?: string }
        this.cachedCartId = parsed.cartId ?? null
        this.cachedSecret = parsed.secret ?? null
      }
    } catch {
      // Corrupted or unavailable storage — ignore
    }
  }

  private persistToStorage(): void {
    if (!this.storageEnabled) return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ cartId: this.cachedCartId, secret: this.cachedSecret }),
      )
    } catch {
      // Storage full or unavailable — SDK still works via in-memory cache
    }
  }

  private clearStorage(): void {
    this.cachedCartId = null
    this.cachedSecret = null
    if (!this.storageEnabled) return
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore
    }
  }
}

/**
 * The 0.1.0 name of {@link Cope}: the same class, so `instanceof` agrees across both names.
 * @deprecated Use `Cope`. `CopeCart` stays exported for code written against 0.1.0.
 */
export const CopeCart = Cope
/** @deprecated Use `Cope`. */
export type CopeCart = Cope
